import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import LocationStamp from "@/components/LocationStamp";
import { LogIn, Coffee, Play, LogOut, Loader2, Clock, Calendar, AlertTriangle } from "lucide-react";

function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  });
}

function ActionButton({ label, icon: Icon, onClick, disabled, tone, testid }) {
  const tones = {
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",
    warning: "bg-[hsl(33,65%,44%)] text-white hover:bg-[hsl(33,65%,40%)]",
    info: "bg-[hsl(200,55%,32%)] text-white hover:bg-[hsl(200,55%,28%)]",
    danger: "bg-[hsl(8,54%,42%)] text-white hover:bg-[hsl(8,54%,38%)]",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`min-h-[96px] rounded-2xl flex flex-col items-center justify-center gap-2 font-heading font-semibold text-lg shadow-md transition-transform active:scale-95 hover:-translate-y-0.5 disabled:opacity-40 disabled:grayscale disabled:pointer-events-none ${tones[tone]}`}
    >
      <Icon className="h-8 w-8" />
      {label}
    </button>
  );
}

export default function StaffDashboard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([api.get("/status/me"), api.get("/shifts/me")]);
    setStatus(s.data);
    setRecent(r.data.slice(0, 5));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/status/me").then((r) => setStatus(r.data)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (url) => {
    if (busy) return;
    setBusy(true);
    try {
      const needsLocation = url === "/clock/in" || url === "/clock/out";
      const location = needsLocation ? await getLocation() : null;
      const { data } = await api.post(url, location || {});
      toast.success(data.message);
      if (needsLocation && !location) {
        toast.info("Location not captured (permission denied or unavailable).");
      }
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const active = status?.active;
  const shift = status?.shift;
  const onBreak = shift?.on_break;
  const alerts = status?.incomplete_alerts || [];

  let workStatus = "Not clocked in";
  if (active && onBreak) workStatus = "On break";
  else if (active) workStatus = "Working";

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold">Hello 👋</h1>
          <p className="text-muted-foreground flex items-center gap-2 mt-1">
            <Calendar className="h-4 w-4" /> {status?.date_display}
            <span className="mx-1">•</span>
            <Clock className="h-4 w-4" /> {status?.server_time_display}
          </p>
        </div>
      </div>

      <Card className="p-6 rounded-2xl border-border shadow-sm">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Status</p>
            <p className="font-heading text-xl font-semibold mt-1" data-testid="work-status">{workStatus}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Clock-in</p>
            <p className="font-heading text-xl font-semibold mt-1" data-testid="clockin-time">
              {shift?.clock_in_display || "—"}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Break</p>
            <p className="font-heading text-xl font-semibold mt-1" data-testid="break-status">
              {onBreak ? "On break" : active ? "Not on break" : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Hours so far</p>
            <p className="font-heading text-xl font-semibold mt-1" data-testid="hours-worked">
              {active ? shift?.live_worked_display : "—"}
            </p>
          </div>
        </div>
      </Card>

      {alerts.length > 0 && (
        <Card className="p-4 rounded-xl border-[hsl(8,50%,80%)] bg-[hsl(8,60%,96%)]" data-testid="missed-clockout-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[hsl(8,54%,42%)] mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-[hsl(8,54%,38%)]">You forgot to clock out</p>
              <p className="text-muted-foreground mt-1">
                {alerts.map((a) => `${a.date_display} (in at ${a.clock_in_display})`).join(", ")}. Please ask your administrator to correct these shifts, or use "Report a Mistake".
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 sm:gap-6">
        <ActionButton label="Clock In" icon={LogIn} tone="primary" testid="clock-in-btn"
          disabled={busy || active} onClick={() => act("/clock/in")} />
        <ActionButton label="Start Break" icon={Coffee} tone="warning" testid="start-break-btn"
          disabled={busy || !active || onBreak} onClick={() => act("/clock/break/start")} />
        <ActionButton label="End Break" icon={Play} tone="info" testid="end-break-btn"
          disabled={busy || !onBreak} onClick={() => act("/clock/break/end")} />
        <ActionButton label="Clock Out" icon={LogOut} tone="danger" testid="clock-out-btn"
          disabled={busy || !active || onBreak} onClick={() => act("/clock/out")} />
      </div>
      {busy && (
        <p className="text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Recording…
        </p>
      )}

      <div>
        <h2 className="font-heading text-xl font-semibold mb-3">Recent shifts</h2>
        <div className="space-y-3" data-testid="recent-shifts">
          {recent.length === 0 && <p className="text-muted-foreground text-sm">No shifts recorded yet.</p>}
          {recent.map((s) => (
            <Card key={s.id} className="p-4 rounded-xl border-border flex items-center justify-between">
              <div>
                <p className="font-medium">{s.date_display}</p>
                <p className="text-sm text-muted-foreground">
                  {s.clock_in_display || "—"} – {s.clock_out_display || "—"} • Break {s.total_break_display}
                </p>
                <div className="flex flex-wrap gap-3 mt-1">
                  {s.clock_in_location && <LocationStamp location={s.clock_in_location} label="Clock-in location" testid={`loc-in-${s.id}`} />}
                  {s.clock_out_location && <LocationStamp location={s.clock_out_location} label="Clock-out location" testid={`loc-out-${s.id}`} />}
                </div>
              </div>
              <div className="text-right">
                <p className="font-heading font-semibold">{s.total_worked_display}</p>
                <StatusBadge status={s.status} />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
